import {
  AUTOMATION_WEEKDAYS,
  type AutomationScheduleFrequency,
} from '../types/AutomationWorkflow.types';

export interface AutomationScheduleConfig {
  frequency: AutomationScheduleFrequency;
  /** Hour of day (0-23). Ignored by the hourly frequency. */
  hour: number;
  minute: number;
  /** 0 = Sunday. Only used by the weekly frequency. */
  dayOfWeek: number;
}

export const defaultScheduleConfig = (): AutomationScheduleConfig => ({
  frequency: 'daily',
  hour: 9,
  minute: 0,
  dayOfWeek: 5,
});

const clamp = (value: unknown, min: number, max: number, fallback: number) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(parsed)));
};

export const parseScheduleConfig = (
  config: Record<string, unknown> | null | undefined,
): AutomationScheduleConfig => {
  const defaults = defaultScheduleConfig();
  const frequency = String(config?.frequency ?? '');

  return {
    frequency:
      frequency === 'hourly' || frequency === 'daily' || frequency === 'weekly'
        ? frequency
        : defaults.frequency,
    hour: clamp(config?.hour, 0, 23, defaults.hour),
    minute: clamp(config?.minute, 0, 59, defaults.minute),
    dayOfWeek: clamp(config?.dayOfWeek, 0, 6, defaults.dayOfWeek),
  };
};

const formatTime = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

export const describeSchedule = (config: AutomationScheduleConfig): string => {
  switch (config.frequency) {
    case 'hourly':
      return `Every hour at :${String(config.minute).padStart(2, '0')}`;
    case 'weekly':
      return `Every ${AUTOMATION_WEEKDAYS[config.dayOfWeek]} at ${formatTime(config.hour, config.minute)}`;
    default:
      return `Every day at ${formatTime(config.hour, config.minute)}`;
  }
};

/** Most recent moment this schedule should have fired, at or before `now`. */
export const lastScheduledOccurrence = (
  config: AutomationScheduleConfig,
  now: Date,
): Date => {
  const candidate = new Date(now);
  candidate.setSeconds(0, 0);

  if (config.frequency === 'hourly') {
    candidate.setMinutes(config.minute);
    if (candidate > now) {
      candidate.setTime(candidate.getTime() - 60 * 60 * 1000);
    }
    return candidate;
  }

  candidate.setHours(config.hour, config.minute, 0, 0);

  if (config.frequency === 'daily') {
    if (candidate > now) candidate.setDate(candidate.getDate() - 1);
    return candidate;
  }

  let guard = 0;
  while (
    (candidate.getDay() !== config.dayOfWeek || candidate > now) &&
    guard < 8
  ) {
    candidate.setDate(candidate.getDate() - 1);
    guard += 1;
  }
  return candidate;
};

/**
 * True when the schedule fired after `since` (the last run, or the moment the
 * automation was enabled) and has not been serviced yet.
 */
export const isScheduleDue = (
  config: AutomationScheduleConfig,
  since: Date | string | null | undefined,
  now: Date = new Date(),
): boolean => {
  const occurrence = lastScheduledOccurrence(config, now);
  if (!since) return false;
  const baseline = since instanceof Date ? since : new Date(since);
  if (Number.isNaN(baseline.getTime())) return false;
  return baseline < occurrence;
};
